import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { semanticColor, type SemanticColor } from "@how/design-system";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type CareStatus = "new" | "assigned" | "in_progress" | "closed";
export const CARE_STATUS_LABEL: Record<CareStatus, string> = {
  new: "New",
  assigned: "Taken",
  in_progress: "In progress",
  closed: "Closed",
};
export const CARE_STATUS_COLOR: Record<CareStatus, SemanticColor> = {
  new: semanticColor("warning"),
  assigned: semanticColor("info"),
  in_progress: semanticColor("info"),
  closed: semanticColor("neutral"),
};
export const CARE_STATUSES = Object.keys(CARE_STATUS_LABEL) as CareStatus[];

export interface CareRow {
  readonly id: string;
  readonly personId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly household: string | null;
  readonly body: string;
  readonly mayContact: boolean;
  readonly confidential: boolean;
  readonly status: CareStatus;
  readonly ownerUserId: string | null;
  readonly ownerName: string | null;
  readonly source: string;
  readonly createdAt: string;
  readonly closedAt: string | null;
  readonly closedNote: string | null;
}

/** The queue: what the caller may read (D13), open first. A church has tens open at once; read whole. */
export function useCareQueue(status: CareStatus | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["care-queue", tenantId, status],
    enabled: tenantId !== null,
    queryFn: async (): Promise<CareRow[]> => {
      const { data, error } = await supabase.rpc("care_queue", {
        p_tenant_id: tenantId ?? "",
        ...(status === null ? {} : { p_status: status }),
      });
      if (error) throw error;
      return data.map((r) => ({
        id: r.request_id,
        personId: r.person_id,
        firstName: r.first_name,
        lastName: r.last_name,
        household: r.household,
        body: r.body,
        mayContact: r.may_contact,
        confidential: r.confidentiality === "confidential",
        status: r.status,
        ownerUserId: r.owner_user_id,
        ownerName: r.owner_name,
        source: r.source,
        createdAt: r.created_at,
        closedAt: r.closed_at,
        closedNote: r.closed_note,
      }));
    },
  });
}

/** The church's active logins, to hand a request to; names come from profiles the church may read. */
export function useChurchLogins() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["church-logins", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<{ userId: string; email: string }[]> => {
      const { data, error } = await supabase.rpc("church_logins", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return data
        .filter((l) => l.status === "active")
        .map((l) => ({ userId: l.user_id, email: l.email }));
    },
  });
}

function useCareInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (personId?: string) => {
    await client.invalidateQueries({ queryKey: ["care-queue", tenantId] });
    if (personId !== undefined)
      await client.invalidateQueries({ queryKey: ["person", tenantId, personId] });
  };
}

export function useAssignCare() {
  const supabase = useSupabase();
  const invalidate = useCareInvalidation();
  return useMutation({
    mutationFn: async (input: { requestId: string; ownerUserId: string | null }) => {
      const { error } = await supabase.rpc("assign_care_request", {
        p_request_id: input.requestId,
        ...(input.ownerUserId === null ? {} : { p_owner_user_id: input.ownerUserId }),
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useSetCareStatus() {
  const supabase = useSupabase();
  const invalidate = useCareInvalidation();
  return useMutation({
    mutationFn: async (input: {
      requestId: string;
      personId: string;
      status: CareStatus;
      note?: string;
    }) => {
      const { error } = await supabase.rpc("set_care_status", {
        p_request_id: input.requestId,
        p_status: input.status,
        ...(input.note === undefined || input.note.trim() === "" ? {} : { p_note: input.note }),
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.personId),
  });
}

export function useLogCare() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useCareInvalidation();
  return useMutation({
    mutationFn: async (input: {
      personId: string;
      body: string;
      mayContact: boolean;
      confidential: boolean;
    }) => {
      const { data, error } = await supabase.rpc("log_care_request", {
        p_tenant_id: tenantId ?? "",
        p_person_id: input.personId,
        p_body: input.body,
        p_may_contact: input.mayContact,
        p_confidential: input.confidential,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_r, input) => invalidate(input.personId),
  });
}

/** People by name for the log dialog: the same list the People screen reads, narrowed by a few letters. */
export function usePeopleOptions(search: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const term = search.trim().replace(/[%_]/g, "");
  return useQuery({
    queryKey: ["people-options", tenantId, term],
    enabled: tenantId !== null && term.length >= 2,
    queryFn: async (): Promise<{ value: string; label: string }[]> => {
      const { data, error } = await supabase
        .from("people")
        .select("id, first_name, last_name")
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`)
        .order("last_name")
        .limit(20);
      if (error) throw error;
      return data.map((p) => ({ value: p.id, label: `${p.first_name} ${p.last_name}` }));
    },
  });
}
